import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  formatObservationFeedback,
  useConditionalBackNavigation,
} from '@taleemabad/shared';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';
import {
  TextField,
  Typography,
  StyledDiv,
  StyledFixedButtonWrapper,
  Button,
  Spacing,
  Progress,
  Select,
  SelectOption,
  Dialog,
  Divider,
  AlertDialog,
  Icon,
  CardContainer,
  Skeleton,
} from '@taleemabad/dsm/components';
import { FeedbackGenerationState } from '@taleemabad/db';

// Built dynamically inside component from minFeedbackLength prop
type FeedbackFormValues = { feedbackText: string };

interface GeneratedFeedback {
  strengths: string[];
  areasOfGrowth: string[];
}

interface ObservationInfo {
  date: string | null;
  className: string | null;
  subject: string | null;
  lesson: string | null;
}

interface TeachingReviewGenerationProps {
  teacherName: string;
  observationInfo: ObservationInfo;
  sections: SelectOption[];
  selectedSection: string;
  feedbackState: FeedbackGenerationState;
  generatedFeedback?: GeneratedFeedback;
  initialFeedbackText: string;
  showIncompleteSectionsDialog: boolean;
  incompleteSections: string[];
  hasAnySectionsFilled?: boolean; // indicates if at least one section has answers
  disabled?: boolean;
  isConnected?: boolean;
  minFeedbackLength?: number; // allow parent to configure validation length
  error?: { message: string; details?: string } | string | null; // flexible error handling - backend can send various error formats
  onIncompleteSectionsDialogClose?: () => void; // close handler for dialog

  // Business logic handlers - these trigger database operations
  onSectionChange: (option: SelectOption) => void;
  onGenerateFeedback: () => Promise<void>;
  onSaveFeedback: (feedbackText: string) => Promise<void>;
  onSendToTeacher: (feedbackText: string) => Promise<void>;
  onViewSummary: (feedbackText: string) => void;
  onBack: () => void;
  onManualWriteClick?: () => void;
  onResetForm?: () => void; // called when parent wants to reset form (e.g., user discards)
  onUnsavedChangesChange?: (hasChanges: boolean) => void; // notify parent when unsaved changes state changes

  // Analytics callbacks - optional event tracking
  onTrackFeedbackEdit?: () => void;
  onTrackFeedbackSaved?: () => void;
}

export const TeachingReviewGeneration: FC<TeachingReviewGenerationProps> = ({
  teacherName,
  observationInfo,
  sections,
  selectedSection,
  feedbackState,
  generatedFeedback,
  initialFeedbackText,
  showIncompleteSectionsDialog,
  incompleteSections,
  hasAnySectionsFilled = false,
  disabled = false,
  isConnected = true,
  minFeedbackLength,
  onIncompleteSectionsDialogClose,
  onSectionChange,
  onGenerateFeedback,
  onSaveFeedback,
  onSendToTeacher,
  onBack,
  onManualWriteClick,
  onResetForm,
  onUnsavedChangesChange,
  onTrackFeedbackEdit,
  onTrackFeedbackSaved,
  onViewSummary,
}) => {
  // Local UI state
  const [showNoInternetPopup, setShowNoInternetPopup] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = useState(false);
  const [showIncompleteWarningDialog, setShowIncompleteWarningDialog] =
    useState(false);
  const [showViewSummaryDialog, setShowViewSummaryDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCurrentlyAIGenerated, setIsCurrentlyAIGenerated] = useState(false);
  const [hasTrackedEdit, setHasTrackedEdit] = useState(false);
  const [baselineFeedbackText, setBaselineFeedbackText] =
    useState(initialFeedbackText);

  // Dynamic schema based on min length (default 10)
  const minLength = minFeedbackLength ?? 10;
  const feedbackSchema = z.object({
    feedbackText: z
      .string()
      .min(1, 'Feedback is required.')
      .min(
        minLength,
        `Feedback must be at least ${minLength} characters long.`,
      ),
  });

  const methods = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { feedbackText: initialFeedbackText },
  });

  const currentFeedbackText = methods.watch('feedbackText');

  // Update form when AI generates feedback
  useEffect(() => {
    if (
      generatedFeedback &&
      feedbackState === FeedbackGenerationState.Generated
    ) {
      const formattedFeedback = formatObservationFeedback(generatedFeedback);
      methods.setValue('feedbackText', formattedFeedback);
      setHasUnsavedChanges(true);
      setIsCurrentlyAIGenerated(true); // Mark as AI generated
      setBaselineFeedbackText(formattedFeedback); // Set baseline for edit tracking
      setHasTrackedEdit(false); // Reset edit tracking for new AI feedback
    }
  }, [generatedFeedback, feedbackState, methods]);

  // Helper function to detect if feedback looks like AI-generated format
  const isAIGeneratedFeedback = (text: string): boolean => {
    if (!text || text.trim().length < 50) return false;

    // AI-generated feedback typically has this structure
    const hasStrengthsSection = text.toLowerCase().includes('strengths:');
    const hasAreasOfGrowthSection =
      text.toLowerCase().includes('areas for growth:') ||
      text.toLowerCase().includes('areas of improvement:');

    // If it has both typical AI sections, likely AI-generated
    return hasStrengthsSection && hasAreasOfGrowthSection;
  };

  // Reset form when initial feedback changes
  useEffect(() => {
    methods.reset({ feedbackText: initialFeedbackText });
    setHasUnsavedChanges(false);
    onUnsavedChangesChange?.(false);
    setHasTrackedEdit(false); // Reset edit tracking for new feedback
    setBaselineFeedbackText(initialFeedbackText); // Update baseline
    // Set AI generated flag based on whether feedback looks like AI format
    // This handles the case when page refreshes with existing AI feedback
    setIsCurrentlyAIGenerated(isAIGeneratedFeedback(initialFeedbackText));
  }, [initialFeedbackText, methods, onUnsavedChangesChange]);

  // Track unsaved changes
  useEffect(() => {
    const subscription = methods.watch((value) => {
      const hasChanges = value.feedbackText !== initialFeedbackText;
      setHasUnsavedChanges(hasChanges);
    });
    return () => subscription.unsubscribe();
  }, [methods, initialFeedbackText]);

  // Notify parent when unsaved changes state changes
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Listen for reset signal from parent (when user clicks Discard)
  // When onResetForm reference changes, reset the form
  useEffect(() => {
    // Reset form to initial state when parent requests it
    methods.reset({ feedbackText: initialFeedbackText });
    setHasUnsavedChanges(false);
    setHasTrackedEdit(false);
    setBaselineFeedbackText(initialFeedbackText);
  }, [onResetForm]);

  // Event handlers
  const handleGenerateFeedback = async () => {
    if (!isConnected) {
      setShowNoInternetPopup(true);
      return;
    }

    // If no sections are filled at all, this shouldn't happen as button should be disabled
    // But as a safety check, block the action
    if (!hasAnySectionsFilled) {
      return;
    }

    // If some sections are filled but incomplete, show warning dialog
    if (hasAnySectionsFilled && incompleteSections.length > 0) {
      setShowIncompleteWarningDialog(true);
      return;
    }

    try {
      await onGenerateFeedback(); // Normal validation
    } catch (error) {
      console.error('Failed to generate feedback:', error);
      // Error is handled by the parent component
    }
  };

  const handleConfirmGenerateWithIncomplete = async () => {
    setShowIncompleteWarningDialog(false);
    try {
      await onGenerateFeedback(); // User confirmed to proceed with incomplete sections
    } catch (error) {
      console.error('Failed to generate feedback:', error);
      // Error is handled by the parent component
    }
  };

  const handleRegenerateFeedback = () => {
    if (!isConnected) {
      setShowNoInternetPopup(true);
      return;
    }
    // For regenerate, always show confirmation dialog first - don't check incomplete sections
    // User already has feedback and wants to replace it
    setShowRegenerateConfirm(true);
  };

  const handleConfirmRegenerate = async () => {
    setShowRegenerateConfirm(false);
    try {
      await onGenerateFeedback(); // Normal validation for regenerate
    } catch (error) {
      console.error('Failed to regenerate feedback:', error);
      // Error is handled by the parent component
    }
  };

  const handleSaveFeedback = async () => {
    const isValid = await methods.trigger();
    if (!isValid) return;

    const feedbackText = methods.getValues('feedbackText');
    try {
      await onSaveFeedback(feedbackText);
      // Track save event
      onTrackFeedbackSaved?.();
      // Clear local unsaved changes flag
      setHasUnsavedChanges(false);
      // Notify parent immediately (parent also clears its flag in handleSaveFeedback)
      onUnsavedChangesChange?.(false);
      // Now navigate - parent's callback should see the cleared flag
      onBack();
    } catch (error) {
      console.error('Save operation failed:', error);
      // Error is handled by the parent component
    }
  };

  const handleConfirmSend = async () => {
    const feedbackText = methods.getValues('feedbackText');
    try {
      await onSendToTeacher(feedbackText);
      // Track send with method type
      setShowSendConfirmDialog(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Send operation failed:', error);
      setShowSendConfirmDialog(false);
      // Error is handled by the parent component
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      onBack();
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    // Reset form to initial state
    methods.reset({ feedbackText: initialFeedbackText });
    setHasUnsavedChanges(false);
    onUnsavedChangesChange?.(false);
    setHasTrackedEdit(false);
    setBaselineFeedbackText(initialFeedbackText);
    // Navigate back
    onBack();
  };

  const handleViewSummaryClick = () => {
    if (hasUnsavedChanges) {
      setShowViewSummaryDialog(true);
    } else {
      // No unsaved changes, navigate directly to summary
      onViewSummary(currentFeedbackText);
    }
  };

  const handleSaveAndViewSummary = async () => {
    // Validate form first
    const isValid = await methods.trigger();
    if (!isValid) return;

    const feedbackText = methods.getValues('feedbackText');
    try {
      // Save to database
      await onSaveFeedback(feedbackText);
      // Clear unsaved flag
      setHasUnsavedChanges(false);
      onUnsavedChangesChange?.(false);
      // Close dialog and navigate to summary
      setShowViewSummaryDialog(false);
      onViewSummary(feedbackText);
    } catch (error) {
      console.error('Save operation failed:', error);
      // Error is handled by the parent component
    }
  };

  const handleViewWithoutSaving = () => {
    // Close dialog and navigate to summary without saving
    setShowViewSummaryDialog(false);
    onViewSummary(currentFeedbackText);
  };

  const handleCancelViewSummary = () => {
    setShowViewSummaryDialog(false);
  };

  // Handle back navigation (both web and native) with unsaved changes logic
  useConditionalBackNavigation({
    hasUnsavedChanges,
    hasOpenDialogs:
      showRegenerateConfirm ||
      showSendConfirmDialog ||
      showIncompleteWarningDialog ||
      showIncompleteSectionsDialog ||
      showViewSummaryDialog,
    onUnsavedChanges: () => setShowUnsavedChangesDialog(true),
    onCloseDialogs: () => {
      closeDialogs();
      onIncompleteSectionsDialogClose?.();
    },
    onNormalBack: onBack,
    enabled: true,
  });

  // Dialog handlers
  const closeDialogs = () => {
    setShowRegenerateConfirm(false);
    setShowUnsavedChangesDialog(false);
    setShowSendConfirmDialog(false);
    setShowIncompleteWarningDialog(false);
    setShowViewSummaryDialog(false);
  };

  const closeNoInternetPopup = () => {
    setShowNoInternetPopup(false);
  };

  const [showManualInput, setShowManualInput] = useState(false);

  // Helper function to determine if we have an error (for internal logic only)
  const hasError = (
    error: { message: string; details?: string } | string | null | undefined,
  ): boolean => {
    return error != null && error !== '';
  };

  // Handle text input changes
  const handleFeedbackTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    methods.setValue('feedbackText', newValue);

    // Track edit event only once when user first modifies existing text
    if (
      !hasTrackedEdit &&
      baselineFeedbackText &&
      newValue !== baselineFeedbackText
    ) {
      onTrackFeedbackEdit?.();
      setHasTrackedEdit(true);
    }
  };

  // Common text field component to avoid duplication
  const renderFeedbackTextField = () => (
    <>
      <TextField
        variant="outlined"
        label="Feedback"
        type="textarea"
        placeholder="Write your feedback here..."
        value={currentFeedbackText}
        onInput={handleFeedbackTextChange}
        error={!!methods.formState.errors.feedbackText}
        errorText={methods.formState.errors.feedbackText?.message}
        rows={12}
        cols={100}
        disabled={disabled}
        readOnly={disabled}
        data-cy="feedback-textarea"
      />

      <Spacing marginTop="lg">
        <Typography variant="textBodySmall">
          {currentFeedbackText.length} characters
        </Typography>
      </Spacing>
    </>
  );

  // Common action buttons component to avoid duplication
  const renderActionButtons = (
    showRegenerateButton = false,
    showGenerateButton = false,
    requireSectionsFilled = false,
  ) => (
    <>
      {showGenerateButton && (
        <Spacing
          marginTop="lg"
          marginBottom={showRegenerateButton ? 'md' : 'xl'}
        >
          {requireSectionsFilled && !hasAnySectionsFilled && (
            <Spacing marginBottom="md">
              <Typography
                variant="textBodySmall"
                textAlign="center"
                color="warning"
              >
                Please fill at least one observation section to generate
                feedback
              </Typography>
            </Spacing>
          )}
          <Button
            variant="outlined"
            onClick={handleGenerateFeedback}
            disabled={
              disabled || (requireSectionsFilled && !hasAnySectionsFilled)
            }
            width="100%"
            data-cy="generate-feedback-button"
          >
            {showRegenerateButton
              ? 'Generate New Feedback'
              : 'Generate Feedback'}
          </Button>
        </Spacing>
      )}

      {showRegenerateButton && (
        <Spacing marginTop={showGenerateButton ? 'md' : 'lg'} marginBottom="xl">
          <Button
            variant="outlined"
            onClick={handleRegenerateFeedback}
            disabled={disabled}
            width="100%"
            data-cy="regenerate-feedback-button"
          >
            Regenerate Feedback
          </Button>
        </Spacing>
      )}

      <StyledFixedButtonWrapper bottom={0} justify="flex-end">
        <StyledResponsiveButtonContainer>
          <Button
            variant="outlined"
            type="button"
            onControlledClick={handleBackClick}
            data-cy="back-button"
          >
            Back
          </Button>
          <Button
            type="button"
            onControlledClick={handleSaveFeedback}
            disabled={disabled}
            data-cy="done-button"
          >
            Done
          </Button>
        </StyledResponsiveButtonContainer>
      </StyledFixedButtonWrapper>
    </>
  );

  const renderReadyState = () => {
    return (
      <StyledReadyContainer>
        {!hasAnySectionsFilled ? (
          <>
            <Typography variant="textTitleMedium" textAlign="center">
              Unable to Generate Feedback
            </Typography>
            <Spacing marginTop="md" marginBottom="xl">
              <Typography variant="textBodyMedium" textAlign="center">
                Please complete at least one observation section or write
                feedback manually.
              </Typography>
            </Spacing>
            <Button
              onClick={handleGenerateFeedback}
              disabled={true}
              data-cy="generate-feedback-button"
            >
              Generate Feedback
            </Button>
            <Spacing marginTop="lg">
              <Button
                variant="outlined"
                onClick={() => {
                  onManualWriteClick?.();
                  setShowManualInput(true);
                }}
                disabled={disabled}
                width="100%"
                data-cy="write-manually-button"
              >
                Write Manually
              </Button>
            </Spacing>
          </>
        ) : (
          <>
            <Typography variant="textTitleMedium" textAlign="center">
              Ready to Generate
            </Typography>
            <Spacing marginTop="md" marginBottom="xl">
              <Typography variant="textBodyMedium" textAlign="center">
                Tap below to create evidence-based feedback or write it manually
              </Typography>
            </Spacing>
            {!hasAnySectionsFilled && (
              <Spacing marginBottom="lg">
                <Typography
                  variant="textBodySmall"
                  textAlign="center"
                  color="warning"
                >
                  Please fill at least one observation section to generate
                  feedback
                </Typography>
              </Spacing>
            )}
            <Button
              onClick={handleGenerateFeedback}
              disabled={disabled || !hasAnySectionsFilled}
              data-cy="generate-feedback-button"
            >
              Generate Feedback
            </Button>
            <Spacing marginTop="lg">
              <Button
                variant="outlined"
                onClick={() => {
                  onManualWriteClick?.();
                  setShowManualInput(true);
                }}
                disabled={disabled}
                width="100%"
                data-cy="write-manually-button"
              >
                Write Manually
              </Button>
            </Spacing>
          </>
        )}
      </StyledReadyContainer>
    );
  };

  const renderLoadingState = () => (
    <StyledLoadingContainer>
      <StyledDiv flexDirection="column" gap={16} width="100%">
        <StyledDiv flexDirection="row" gap={12} alignItem="center">
          <Icon name="potted_plant" color="--md-sys-color-primary" />
          <StyledDiv flexDirection="column" gap={4}>
            <Typography variant="textTitleMedium">
              Preparing your review
            </Typography>
            <Typography variant="textBodySmallOnSurfaceVariant">
              Feel free to continue your work while we finish.
            </Typography>
          </StyledDiv>
        </StyledDiv>

        <StyledDiv flexDirection="column" gap={12} marginTop={12}>
          <Skeleton height="20px" width="100%" />
          <Skeleton height="20px" width="90%" />
          <Skeleton height="20px" width="95%" />
          <Skeleton height="64px" width="100%" />
        </StyledDiv>

        <StyledDiv marginTop={24} width="100%">
          <Progress
            type="linear"
            indeterminate={true}
            emotionCss={css`
              width: 100%;
              md-linear-progress {
                width: 100%;
              }
            `}
          />
        </StyledDiv>
      </StyledDiv>
    </StyledLoadingContainer>
  );

  return (
    <>
      <Spacing marginTop="xl">
        <Typography variant="textTitleMedium">
          Class Insights: {teacherName}
        </Typography>
      </Spacing>

      {/* Sections Dropdown */}
      <Spacing marginTop="xl">
        <Select
          options={sections}
          defaultValue={selectedSection}
          onChange={onSectionChange}
          variant="outlined"
          label="Sections"
          disabled={disabled}
          width="100%"
        />
      </Spacing>

      {/* Observation Info */}
      <Spacing marginTop="md" marginBottom="xl">
        <StyledDiv flexDirection="column" gap={4}>
          <Typography variant="textBodyMedium">
            Date: {observationInfo.date}
          </Typography>
          <Typography variant="textBodyMedium">
            Class: {observationInfo.className}
          </Typography>
          <Typography variant="textBodyMedium">
            Subject: {observationInfo.subject}
          </Typography>
          <Typography variant="textBodyMedium">
            Lesson: {observationInfo.lesson}
          </Typography>
        </StyledDiv>
      </Spacing>

      <Divider />

      {/* Content based on state */}
      <Spacing marginTop="lg">
        {feedbackState === FeedbackGenerationState.Ready &&
          !showManualInput &&
          renderReadyState()}
        {feedbackState === FeedbackGenerationState.Loading &&
          renderLoadingState()}
        {(feedbackState === FeedbackGenerationState.Generated ||
          (feedbackState === FeedbackGenerationState.Ready &&
            showManualInput)) && (
          <>
            <Typography variant="textTitleMedium">Feedback Editor</Typography>
            <Spacing marginTop="sm" marginBottom="lg">
              <Typography variant="textBodyMedium">
                You can write feedback manually or generate automatically
              </Typography>
            </Spacing>

            {renderFeedbackTextField()}
            {renderActionButtons(
              isCurrentlyAIGenerated, // showRegenerate button when AI feedback exists
              !isCurrentlyAIGenerated, // show Generate only before first AI generation
              true, // require sections to be filled for Generate button
            )}
            <Button
              variant="text"
              onClick={handleViewSummaryClick}
              width="100%"
              data-cy="view-summary-button"
            >
              View Summary
            </Button>
          </>
        )}
        {feedbackState === FeedbackGenerationState.Error && (
          <>
            <StyledDiv flexDirection="column" alignItem="center">
              <Typography variant="textBodyMedium" textAlign="center">
                Unable to generate feedback. You can try again or write feedback
                manually below.
              </Typography>

              {!hasAnySectionsFilled && (
                <Spacing marginTop="md">
                  <Typography
                    variant="textBodySmall"
                    textAlign="center"
                    color="warning"
                  >
                    Please fill at least one observation section to try again
                  </Typography>
                </Spacing>
              )}

              <Spacing marginTop="lg" marginBottom="lg">
                <Button
                  variant="outlined"
                  onClick={handleGenerateFeedback}
                  disabled={disabled || !hasAnySectionsFilled}
                  width="100%"
                  data-cy="try-again-button"
                >
                  Try Again
                </Button>
              </Spacing>
            </StyledDiv>

            {renderFeedbackTextField()}
            {renderActionButtons(
              isCurrentlyAIGenerated, // showRegenerate button when AI feedback exists
              !isCurrentlyAIGenerated, // show Generate only before first AI generation
              true, // require sections to be filled for Generate button
            )}
          </>
        )}
      </Spacing>

      {/* Incomplete Sections Dialog */}
      <Dialog
        open={showIncompleteSectionsDialog}
        onClose={onIncompleteSectionsDialogClose}
        title="Incomplete Observation"
      >
        <StyledDiv flexDirection="column" gap={16}>
          <Typography variant="textBodyMedium">
            The following sections are not completed:
          </Typography>
          {incompleteSections.map((section, index) => (
            <Typography key={index} variant="textBodyMedium">
              • {section}
            </Typography>
          ))}
          <Typography variant="textBodyMedium">
            Complete the missing sections to continue generating feedback?
          </Typography>
          <Button onClick={onIncompleteSectionsDialogClose}>Got it</Button>
        </StyledDiv>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog
        open={showSendConfirmDialog}
        onClose={closeDialogs}
        title="Sending feedback to teacher"
      >
        <StyledDiv flexDirection="column" gap={16}>
          <Typography variant="textBodyMedium">
            Are you sure you want to send this feedback to the teacher?
          </Typography>
          <StyledDiv flexDirection="row" gap={8} justifyContent="flex-end">
            <Button variant="outlined" onClick={closeDialogs}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSend}>Send Feedback</Button>
          </StyledDiv>
        </StyledDiv>
      </Dialog>

      {/* No Internet Connection Dialog */}
      <AlertDialog
        hasBackdrop={false}
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNoInternetPopup}
        onClosePopup={closeNoInternetPopup}
      />

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog
        hasBackdrop={false}
        title="Regenerate Feedback"
        description="Are you sure you want to regenerate the feedback? This will replace the current feedback."
        showPopup={showRegenerateConfirm}
        secondaryButton={true}
        primaryButton={true}
        secondaryButtonText="Cancel"
        primaryButtonText="Regenerate"
        onSecondaryButtonClick={closeDialogs}
        onPrimaryButtonClick={handleConfirmRegenerate}
        onClosePopup={closeDialogs}
      />

      {/* Unsaved Changes Dialog */}
      <AlertDialog
        hasBackdrop={false}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to go back without saving?"
        showPopup={showUnsavedChangesDialog}
        secondaryButton={true}
        primaryButton={true}
        secondaryButtonText="Cancel"
        primaryButtonText="Discard"
        onSecondaryButtonClick={closeDialogs}
        onPrimaryButtonClick={handleDiscardChanges}
        onClosePopup={closeDialogs}
      />

      {/* Incomplete Sections Warning Dialog */}
      <AlertDialog
        hasBackdrop={false}
        title="Incomplete Sections"
        description="Some sections are incomplete. Are you sure you want to continue to generate feedback?"
        showPopup={showIncompleteWarningDialog}
        secondaryButton={true}
        primaryButton={true}
        secondaryButtonText="Cancel"
        primaryButtonText="Continue"
        onSecondaryButtonClick={closeDialogs}
        onPrimaryButtonClick={handleConfirmGenerateWithIncomplete}
        onClosePopup={closeDialogs}
      />

      {/* View Summary with Unsaved Changes Dialog */}
      <AlertDialog
        hasBackdrop={false}
        title="Unsaved Changes"
        description="You have unsaved changes. What would you like to do?"
        showPopup={showViewSummaryDialog}
        secondaryButton={true}
        primaryButton={true}
        secondaryButtonText="Cancel"
        primaryButtonText="View Without Saving"
        onSecondaryButtonClick={handleCancelViewSummary}
        onPrimaryButtonClick={handleViewWithoutSaving}
        onClosePopup={handleCancelViewSummary}
      >
        <Button variant="text" width="100%" onClick={handleSaveAndViewSummary}>
          Save & View Summary
        </Button>
      </AlertDialog>
    </>
  );
};

// Styled Components
const StyledReadyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--rk-spacing-xl) 0;
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--rk-spacing-xl) 0;
  min-height: 200px;
`;

const StyledResponsiveButtonContainer = styled.div`
  width: calc(100% - 272px);
  display: flex;
  justify-content: space-between;

  ${mq(BreakpointSizes.mobile)} {
    width: 100%;
  }
`;
